import { motion } from "framer-motion";
import { useRef } from "react";
import { useScroll, useTransform } from "framer-motion";

const LINES = ["DELTA DL", "ENTERPRISE, LLC"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.5 + i * 0.18, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ onEnroll }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacityText = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden bg-[#050505]"
    >
      {/* Background video */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
        <video
          data-testid="hero-bg-video"
          className="h-full w-full object-cover opacity-45"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1920&q=80"
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#050505]" />
      </motion.div>

      <motion.div
        style={{ opacity: opacityText }}
        className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-center px-6 md:px-12"
      >
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-6 text-xs uppercase tracking-[0.4em] text-[#7C3AED]"
        >
          Nursing Algebra · Books · Arts · Leadership
        </motion.span>

        <h1 className="font-display uppercase leading-[0.85] tracking-tighter">
          {LINES.map((line, i) => (
            <span key={i} className={`block overflow-hidden ${i === 0 ? "mb-4 md:mb-6" : ""}`}>
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block text-[16vw] md:text-[13vw] text-white"
              >
                {line.split(",").map((part, j, arr) => (
                  <span key={j}>
                    {part}
                    {j < arr.length - 1 && (
                      <span style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>,</span>
                    )}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-lg text-white/70 leading-relaxed">
            A studio for serious learners. Five disciplines, one standard —
            taught with craft, taught with conviction.
          </p>
          <button
            data-testid="hero-enroll-btn"
            onClick={onEnroll}
            className="magnetic-btn self-start rounded-full bg-white px-9 py-4 text-sm font-semibold uppercase tracking-wider text-black hover:bg-[#7C3AED] hover:text-white"
          >
            Begin Your Application
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-white/40"
      >
        Scroll to explore
      </motion.div>
    </section>
  );
};

export default Hero;
